import React, { useState } from 'react';
import styled from 'styled-components';

const StartMenuItem = styled.div`
  padding: 8px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  cursor: pointer;
  font-family: 'MS Sans Serif', sans-serif;
  
  &:hover {
    background-color: #000080;
    color: white;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 14px;
    gap: 6px;
  }
`;

const StartMenuContainer = styled.div`
  position: fixed;
  bottom: 40px;
  left: 0;
  background-color: #c0c0c0;
  border: 2px solid;
  border-color: #ffffff #808080 #808080 #ffffff;
  display: flex;
  z-index: 1000;
  min-width: 400px;
  box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
    max-height: 80vh;
    overflow-y: auto;
  }
`;

const MenuBanner = styled.div`
  width: 65px;
  background: linear-gradient(180deg, #000080 0%, #1084d0 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  
  img {
    width: 36px;
    height: 36px;
    margin-bottom: 20px;
  }
  
  span {
    color: white;
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    font-weight: bold;
    letter-spacing: 3px;
    font-size: 18px;
    font-family: 'MS Sans Serif', sans-serif;
  }
`;

const MenuContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 2px;
`;

const MenuSection = styled.div`
  margin: 4px 0;
`;

const MenuDivider = styled.div`
  height: 2px;
  background: #808080;
  margin: 8px 0;
`;

const SubMenu = styled.div`
  position: absolute;
  left: 100%;
  top: 0;
  background-color: #c0c0c0;
  border: 2px solid;
  border-color: #ffffff #808080 #808080 #ffffff;
  display: none;
  min-width: 180px;

  @media (max-width: 768px) {
    position: static;
    left: unset;
    top: unset;
    border: none;
    border-left: 2px solid #808080;
    margin-left: 20px;
    margin-top: 4px;
  }
`;

const MenuItem = styled.div`
  padding: 12px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  cursor: pointer;
  font-size: 16px;
  
  img {
    width: 28px;
    height: 28px;
  }
  
  &:hover {
    background-color: #000080;
    color: white;
  }
  
  &:hover > ${SubMenu} {
    display: block;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    
    /* Show submenu on click instead of hover for mobile */
    &:hover > ${SubMenu} {
      display: none;
    }
    
    &.active > ${SubMenu} {
      display: block;
    }
  }
`;

const UserSection = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid #808080;
  
  img {
    width: 48px;
    height: 48px;
    border: 2px solid #ffffff;
  }
  
  span {
    font-weight: bold;
    font-size: 16px;
  }
`;

// Create a new styled component for submenu items
const SubMenuItem = styled.div`
  padding: 12px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  cursor: pointer;
  font-size: 16px;
  color: black; // Set default color to black
  
  img {
    width: 28px;
    height: 28px;
  }
  
  &:hover {
    background-color: #000080;
    color: white; // Only change to white on hover
  }
`;

export const StartMenu = ({ onItemClick }) => {
  const [activeMenuItem, setActiveMenuItem] = useState(null);

  const handleMenuItemClick = (item, index) => {
    if (window.innerWidth <= 768) {
      // For mobile: toggle submenu visibility
      if (item.submenu) {
        setActiveMenuItem(activeMenuItem === index ? null : index);
      } else {
        onItemClick(item);
      }
    } else if (!item.submenu) {
      // For desktop: only trigger click for items without submenu
      onItemClick(item);
    }
  };

  const menuItems = [
    {
      label: 'Programs',
      icon: '/images/icons/programs.svg',
      submenu: [
        { icon: '/images/icons/notepad.svg', label: 'Notepad' },
        { icon: '/images/icons/cmd.svg', label: 'Command Prompt' },
        { icon: '/images/icons/chess.svg', label: 'Chess' }
      ]
    },
    {
      label: 'Documents',
      icon: '/images/icons/folder.svg',
      submenu: [
        { icon: '/images/icons/resume.svg', label: 'Resume' },
        { icon: '/images/icons/projects.svg', label: 'Projects' }
      ]
    },
    { type: 'divider' },
    { icon: '/images/icons/about.svg', label: 'About Me' },
    { icon: '/images/icons/help.svg', label: 'Help' }
  ];

  return (
    <StartMenuContainer>
      <MenuBanner>
        <img src="/images/dOSlogo.svg" alt="DavidOS" />
        <span>DavidOS</span>
      </MenuBanner>
      
      <MenuContent>
        <UserSection>
          <img src="/images/icons/user.svg" alt="User" />
          <span>Guest</span>
        </UserSection>
        
        {menuItems.map((item, index) => (
          item.type === 'divider' ? (
            <MenuDivider key={index} />
          ) : (
            <MenuItem 
              key={index} 
              onClick={() => handleMenuItemClick(item, index)}
              className={activeMenuItem === index ? 'active' : ''}
            >
              <img src={item.icon} alt="" />
              {item.label}
              {item.submenu && (
                <SubMenu>
                  {item.submenu.map((subItem, subIndex) => (
                    <SubMenuItem 
                      key={subIndex} 
                      onClick={(e) => {
                        e.stopPropagation();
                        onItemClick(subItem);
                      }}
                    >
                      <img src={subItem.icon} alt="" />
                      {subItem.label}
                    </SubMenuItem>
                  ))}
                </SubMenu>
              )}
            </MenuItem>
          )
        ))}
      </MenuContent>
    </StartMenuContainer>
  );
}; 